import { getValFromObject, VAR_TYPES } from "../config/FeatureDashboardVarMap";

export const hasFeatureControlData = (feature, featureVars, customisationVars) => {
    const featureId = feature?._id;
    return Boolean(featureId && featureVars?.current?.[featureId] && customisationVars?.current?.[featureId]);
}

export const FindParamArrayFromCustomisations = (customisationVars, featureId, paramString) => {
    // Find our param string in default and see if it is an array of options...
    // console.log('Finding customisation: ', customisationVars, paramString, featureId);
    if (customisationVars?.default[featureId]) {
        for (let prop1 in customisationVars.default[featureId]) {
            for (let prop2 in customisationVars.default[featureId][prop1]) {
                for (let prop3 in customisationVars.default[featureId][prop1][prop2]) {
                    // console.log('Testing param prop3: ', prop3, paramString, customisationVars.current[featureId][prop1][prop2][prop3]);
                    if (prop3 === paramString && Array.isArray(customisationVars.default[featureId][prop1][prop2][prop3])) {
                        // We found a match!
                        console.log('Found customisation array: ', customisationVars.default[featureId][prop1][prop2][prop3]);
                        return customisationVars.current[featureId][prop1][prop2][prop3];
                    }
                }
            }
        }
    }
    return null;
}

export const syncVideoDisruptorControlFeatureVars = (featureVarsIn, featureData, customisationVars) => {
    const featureKey = featureData?.varKey || featureData?.feature?.varKey || featureData?.feature?.featureKey;
    if (featureKey !== 'disruptorVideo') {
        return false;
    }

    let featureVars = featureVarsIn;
    if (featureVars?.current) {
        featureVars = featureVars.current;
    }
    if (featureVars?.[featureData._id]) {
        featureVars = featureVars[featureData._id];
    }
    if (!featureVars || typeof featureVars !== 'object') {
        return false;
    }

    const liveControls = featureVars.controls || (featureVars.controls = {});
    const customControls = getValFromObject(customisationVars?.current ?? customisationVars, featureData._id, 'controls.controls');
    if (!customControls || typeof customControls !== 'object') {
        return false;
    }

    let madeChanges = false;
    ['volume', 'loop', 'numberOfLoops', 'recordAnalytics'].forEach((controlKey) => {
        const customControlValue = Array.isArray(customControls[controlKey])
            ? customControls[controlKey][0]
            : customControls[controlKey];
        if (typeof customControlValue !== 'undefined' && liveControls[controlKey] !== customControlValue) {
            liveControls[controlKey] = customControlValue;
            madeChanges = true;
        }
    });

    return madeChanges;
}

// Copy only live variables that explicitly source their value from campaign
// customisation. This is used after an editor save so a running feature receives
// typography (and other configured) changes without resetting unrelated live
// controls to their defaults.
export const syncCustomisationBackedFeatureVars = (featureVarsIn, featureData, customisationVars) => {
    let featureVars = featureVarsIn?.current ?? featureVarsIn;
    if (featureVars?.[featureData?._id]) {
        featureVars = featureVars[featureData._id];
    }

    if (!featureVars || !featureData?.feature?.defaultVars) {
        return false;
    }

    let madeChanges = false;
    for (const varGroupConfig of featureData.feature.defaultVars) {
        const varGroup = featureVars[varGroupConfig.varKey] || (featureVars[varGroupConfig.varKey] = {});
        for (const varConfig of varGroupConfig.variables || []) {
            if (typeof varConfig?.useCustomisationValueOnLoad !== 'string' || !varConfig.useCustomisationValueOnLoad) {
                continue;
            }

            const customisationValue = getValFromObject(
                customisationVars?.current ?? customisationVars,
                featureData._id,
                varConfig.useCustomisationValueOnLoad
            );
            if (typeof customisationValue !== 'undefined' && varGroup[varConfig.varName] !== customisationValue) {
                varGroup[varConfig.varName] = customisationValue;
                madeChanges = true;
            }
        }
    }

    return madeChanges;
}

export const resetOnLoadDefaultFeatureVars = (featureVarsIn, featureData, customisationVars) => {
    let featureVars = featureVarsIn;
    if (featureVars.current) {
        featureVars = featureVars.current;
    }
    if (featureVars[featureData._id]) {
        featureVars = featureVars[featureData._id];
    }
    // Any feature vars that are configured in featureData to resetDefaultOnLoad, we need to reset to the default value
    // console.log('[resetOnLoadDefaultFeatureVars] ' + featureData.contentLabel + ' Feature vars: ', featureVarsIn, featureVars, featureData);
    let madeChanges = false;
    for (let i = 0; i < featureData.feature.defaultVars.length; i++) {
        let varGroupConfig = featureData.feature.defaultVars[i];
        // console.log('Checking feature var group: ', varGroupConfig);
        for (let j = 0; j < varGroupConfig.variables.length; j++) {
            let varConfig = varGroupConfig.variables[j];
            // console.log('Checking feature var: ', varGroupConfig, varConfig, featureVars[varGroupConfig.varKey][varConfig.varName], varConfig.defaultVal);
            // A field that explicitly declares a customisation source must be
            // hydrated into the live feature, even when it is not otherwise
            // configured to reset to its default on load. Typography fields
            // commonly use this path, and leaving it behind meant the canvas
            // had the saved font while the live feature retained "Roboto".
            const customisationSource = typeof varConfig?.useCustomisationValueOnLoad === 'string'
                && varConfig.useCustomisationValueOnLoad !== '';
            if (varConfig.resetDefaultOnLoad || customisationSource) {
                // console.log('Needs to be reset: ', varConfig.varName);
                
                // We only reset these vars if we don't have a stream level override, otherwise stuff can go out of sync!
                const streamLevelOverrideKeyString = featureData._id + '.' + varGroupConfig.varKey + '.' + varConfig.varName;
                let ignoreReset = false;
                if (featureVarsIn.streamLevelOverrideKeys && featureVarsIn.streamLevelOverrideKeys.includes(streamLevelOverrideKeyString)) {
                    ignoreReset = true;
                }

                if (!ignoreReset) {
                    if (customisationSource) {
                        const customisationValue = getValFromObject(
                            customisationVars?.current ?? customisationVars,
                            featureData._id,
                            varConfig.useCustomisationValueOnLoad
                        );
                        if (typeof customisationValue !== 'undefined'
                            && featureVars[varGroupConfig.varKey][varConfig.varName] !== customisationValue) {
                            featureVars[varGroupConfig.varKey][varConfig.varName] = customisationValue;
                            // console.log('Set live value to customisation value: ', customisationValue, featureVars[varGroupConfig.varKey][varConfig.varName]);
                            madeChanges = true;
                        }
                    } else
                    if (featureVars[varGroupConfig.varKey][varConfig.varName] !== varConfig.defaultVal) {
                        featureVars[varGroupConfig.varKey][varConfig.varName] = varConfig.defaultVal;
                        // console.log('Set live value to default value: ', varConfig.defaultVal, featureVars[varGroupConfig.varKey][varConfig.varName]);
                        madeChanges = true;
                    }
                } else {
                    // console.log('Ignoring reset as we have a stream level override: ', streamLevelOverrideKeyString);
                }
            }
        }
    }
    if (syncVideoDisruptorControlFeatureVars(featureVarsIn, featureData, customisationVars)) {
        madeChanges = true;
    }
    return madeChanges;
}

/**
 * Deeply merge two objects together, overwriting values in obj1 with values in obj2.
 * @param {*} obj1 
 * @param {*} obj2 
 * @returns 
 */
export const deepObjectMerge = (obj1, obj2, replaceArrays = true, alwaysReplaceProps = []) => {
    // console.log('Deep object merge: ', JSON.parse(JSON.stringify(obj1)), JSON.parse(JSON.stringify(obj2)));
    // console.log('Replace arrays: ', replaceArrays);
    if (obj2.mergeMode) {
        replaceArrays = obj2.mergeMode === 'replaceArrays';
        // console.log('Replace arrays from obj2: ', replaceArrays);
    } else
    if (obj1.mergeMode) {
        replaceArrays = obj1.mergeMode === 'replaceArrays';
        // console.log('Replace arrays from obj1: ', replaceArrays);
    }
    if (obj2.alwaysReplaceProps) {
        alwaysReplaceProps = obj2.alwaysReplaceProps;
        // console.log('Always replace props from obj2: ', alwaysReplaceProps);
    } else
    if (obj1.alwaysReplaceProps) {
        alwaysReplaceProps = obj1.alwaysReplaceProps;
        // console.log('Always replace props from obj1: ', alwaysReplaceProps);
    }
    for (let key in obj2) {
        if (key === 'mergeMode' || key === 'alwaysReplaceProps') {
            continue;
        }
        if (alwaysReplaceProps.includes(key)) {
            obj1[key] = obj2[key];
        } else
        if (Array.isArray(obj2[key]) && replaceArrays) {
            obj1[key] = JSON.parse(JSON.stringify(obj2[key]));
        } else
        if (obj2[key] !== null && typeof obj2[key] === 'object') {
            if (obj1[key] === undefined || obj1[key] === null || typeof obj1[key] !== 'object') {
                obj1[key] = JSON.parse(JSON.stringify(obj2[key]));
            } else {
                deepObjectMerge(obj1[key], obj2[key], replaceArrays, alwaysReplaceProps);
            }
        } else {
            if (typeof obj2[key] !== 'undefined') {
                obj1[key] = obj2[key];
            }
        }
    }
    // console.log('Deep object merge result: ', obj1);
    return obj1;
}

export const shouldValueBeStored = (type) => {
    if (FEATURE_CONTROLS_TYPES[type]) {
        return FEATURE_CONTROLS_TYPES[type].isCosmetic ? false : true;
    }
    for (let key in FEATURE_CONTROLS_TYPES) {
        if (FEATURE_CONTROLS_TYPES[key].type === type) {
            return FEATURE_CONTROLS_TYPES[key].isCosmetic ? false : true;
        }
    }
    return true;
}

export const FEATURE_CONTROLS_TYPES = {
    String: {
        type: "string",
        label: "String",
        defaultVarType: VAR_TYPES.stringType,
        liveControl: true,
        newFormatCustomisationField: true,
    },
    Number: {
        type: "number",
        label: "Number",
        defaultVarType: VAR_TYPES.numberType,
        liveControl: true,
        newFormatCustomisationField: true,
    },
    Input: {
        type: "input",
        label: "Input",
        defaultVarType: VAR_TYPES.stringType,
        liveControl: true,
    },
    TextArea: {
        type: "textarea",
        label: "Text Area",
        defaultVarType: VAR_TYPES.stringType,
        liveControl: true,
        newFormatCustomisationField: true,
    },
    Range: {
        type: "slider",
        label: "Range Slider",
        defaultVarType: VAR_TYPES.numberType,
        liveControl: true,
        newFormatCustomisationField: true,
    },
    Toggle: {
        type: "toggle",
        label: "Toggle",
        defaultVarType: VAR_TYPES.booleanType,
        liveControl: true,
        newFormatCustomisationField: true,
    },
    ToggleCheck: {
        type: "toggle-check",
        label: "Toggle Check",
        defaultVarType: VAR_TYPES.booleanType,
        liveControl: true,
        newFormatCustomisationField: true,
    },
    CheckBox: {
        type: "check-box",
        label: "Check Box",
        defaultVarType: VAR_TYPES.booleanType,
        liveControl: true,
    },
    DropDown: {
        type: "dropdown",
        label: "Drop Down",
        defaultVarType: VAR_TYPES.numberType,
        liveControl: true,
        noMinMax: true,
        newFormatCustomisationField: true,
    },
    Radio: {
        type: "radio",
        label: "Radio (Horizontal)",
        defaultVarType: VAR_TYPES.numberType,
        liveControl: true,
        noMinMax: true,
        newFormatCustomisationField: true,
    },
    RadioVertical: {
        type: "radio_v",
        label: "Radio (Vertical)",
        defaultVarType: VAR_TYPES.numberType,
        liveControl: true,
        noMinMax: true,
        newFormatCustomisationField: true,
    },
    HmsTime: {
        type: "hms-time",
        label: "HMS Time of Day Field",
        defaultVarType: VAR_TYPES.numberType,
        liveControl: true,
        noMinMax: true,
        newFormatCustomisationField: true,
    },
    MsTime: {
        type: "ms-time",
        label: "MS Time Field",
        defaultVarType: VAR_TYPES.numberType,
        liveControl: true,
        noMinMax: true,
        newFormatCustomisationField: true,
    },
    RelHmsTime: {
        type: "rel-hms-time",
        label: "Relative HMS Time Field",
        defaultVarType: VAR_TYPES.numberType,
        liveControl: true,
        noMinMax: true,
        newFormatCustomisationField: true,
    },
    DatePicker: {
        type: "date-picker",
        label: "Date Picker",
        defaultVarType: VAR_TYPES.numberType,
        liveControl: false,
        noMinMax: true,
        newFormatCustomisationField: true,
    },
    DateTimePicker: {
        type: "datetime-picker",
        label: "Date Time Picker",
        defaultVarType: VAR_TYPES.stringType,
        fixedDefaultValue: 0,
        liveControl: false,
        noMinMax: true,
        newFormatCustomisationField: true,
    },
    ColourPicker: {
        type: "colour",
        label: "Color Picker",
        defaultVarType: VAR_TYPES.stringType,
        liveControl: false,
        noMaxLength: true,
        newFormatCustomisationField: true,
    },
    VideoUpload: {
        type: "video",
        label: "Video Uploader",
        validateSubProp: "filename",
        liveControl: false,
        newFormatCustomisationField: true,
    },
    ImageUpload: {
        type: "image",
        label: "Image Uploader",
        validateSubProp: "filename",
        liveControl: false,
        newFormatCustomisationField: true,
    },
    ImagePreview: {
        type: "image-preview",
        label: "Image Preview",
        noVal: true,
        liveControl: false,
        newFormatCustomisationField: true,
    },
    AudioUpload: {
        type: "audio",
        label: "Audio Uploader",
        validateSubProp: "filename",
        liveControl: false,
        newFormatCustomisationField: true,
    },
    SubTitle: {
        type: "subtitle",
        label: "Sub Title",
        defaultVarType: VAR_TYPES.stringType,
        liveControl: true,
        noLabelNote: true,
        noDefaultVal: true,
        isCosmetic: true,
    },
    CollapsibleSection: {
        type: "collapsible-section",
        label: "Collapsible Section",
        defaultVarType: VAR_TYPES.stringType,
        liveControl: false,
        noLabelNote: true,
        noDefaultVal: true,
        isCosmetic: true,
        sectionEndTypes: [
            "collapsible-section",
            "collapsible-section-end",
            'column-break',
            'section-break',
        ]
    },
    CollapsibleSectionEnd: {
        type: "collapsible-section-end",
        label: "Collapsible Section End",
        defaultVarType: VAR_TYPES.stringType,
        liveControl: false,
        noLabelNote: true,
        noDefaultVal: true,
        isCosmetic: true,
    },
    DescriptionString: {
        type: 'description-string',
        label: "Info Text",
        liveControl: true,
        noLabel: true,
        noLabelNote: true,
        noDefaultVal: true,
        isCosmetic: true,
        newFormatCustomisationField: true,
    },
    LightDescriptionString: {
        type: 'light-description-string',
        label: "Light Info Text",
        liveControl: true,
        noLabel: true,
        noLabelNote: true,
        noDefaultVal: true,
        isCosmetic: true,
    },
    WarningDescriptionString: {
        type: 'warning-description-string',
        label: "Warning Info Text",
        liveControl: true,
        noLabel: true,
        noLabelNote: true,
        noDefaultVal: true,
        isCosmetic: true,
    },
    ConfirmDescriptionString: {
        type: 'confirm-description-string',
        label: "Confirm Info Text",
        liveControl: true,
        noLabel: true,
        noLabelNote: true,
        noDefaultVal: true,
        isCosmetic: true,
    },
    AttentionDescriptionString: {
        type: 'attention-description-string',
        label: "Attention Info Text",
        liveControl: true,
        noLabel: true,
        noLabelNote: true,
        noDefaultVal: true,
        isCosmetic: true,
    },
    Spacer: {
        type: "spacer",
        label: "Spacer",
        noVal: true,
        noLabel: true,
        noDefaultVal: true,
        liveControl: false,
        isCosmetic: true,
    },
    LineSpacer: {
        type: "line-spacer",
        label: "Line Spacer",
        noVal: true,
        noLabel: true,
        noDefaultVal: true,
        liveControl: true,
        isCosmetic: true,
    },
    Disruptor: {
        type: 'disruptor',
        label: "Select Disruptor",
        liveControl: false,
        noDefaultVal: true,
        newFormatCustomisationField: true,
    },
    Quiz: {
        type: 'quiz',
        label: "Select Quiz",
        liveControl: false,
        noDefaultVal: true,
        newFormatCustomisationField: true,
    },
    Font: {
        type: 'font',
        label: "Select Font",
        liveControl: false,
        noDefaultVal: false,
        newFormatCustomisationField: true,
    },
    LeaderboardData: {
        type: 'leaderboardData',
        label: 'Leaderboard Data',
        defaultVarType: VAR_TYPES.customisationType,
        liveControl: true,
        noDefaultVal: true,
        isCosmetic: true,
    },
    CompChallengeList: {
        type: 'compChallengeList',
        label: 'Competitive Challenge Sets',
        defaultVarType: VAR_TYPES.customisationType,
        liveControl: true,
        noDefaultVal: true,
        isCosmetic: true,
    },
    ClawCameraFeed: {
        type: 'clawCameraFeed',
        label: 'Claw Camera Feed',
        defaultVarType: VAR_TYPES.customisationType,
        liveControl: true,
        noDefaultVal: true,
        isCosmetic: true,
    },
    ClawPollPositionsSelector: {
        type: 'clawPollPositionsSelector',
        label: 'Claw Poll Positions Selector',
        defaultVarType: VAR_TYPES.customisationType,
        liveControl: true,
        noDefaultVal: true,
        isCosmetic: true,
    },
    ClawWinTriggerSelector: {
        type: 'clawWinTriggerSelector',
        label: 'Claw Win Trigger Selector',
        defaultVarType: VAR_TYPES.customisationType,
        liveControl: true,
        noDefaultVal: true,
        isCosmetic: true,
    },
    ClawLoseTriggerSelector: {
        type: 'clawLoseTriggerSelector',
        label: 'Claw Lose Trigger Selector',
        defaultVarType: VAR_TYPES.customisationType,
        liveControl: true,
        noDefaultVal: true,
        isCosmetic: true,
    },
    ColumnBreak: {
        type: 'column-break',
        label: 'Column Break',
        noVal: true,
        noLabel: true,
        noDefaultVal: true,
        liveControl: false,
        isCosmetic: true,
    },
    SectionBreak: {
        type: 'section-break',
        label: 'Row Break',
        noVal: true,
        noLabel: true,
        noDefaultVal: true,
        liveControl: false,
        isCosmetic: true,
    },
    CopySettingsButton: {
        type: 'copy-settings-button',
        label: 'Copy Settings Button',
        noVal: false,
        noLabel: false,
        noLabelNote: true,
        noDefaultVal: false,
        defaultVarType: VAR_TYPES.stringType,
        liveControl: false,
        isCosmetic: true,
    },
    LivePollSelector: {
        type: 'livePollSelector',
        label: 'Live Poll Selector',
        defaultVarType: VAR_TYPES.numberType,
        liveControl: true,
    },
    LivePollAnswerSelector: {
        type: 'livePollAnswerSelector',
        label: 'Live Poll Answer Selector',
        defaultVarType: VAR_TYPES.customisationType,
        liveControl: true,
    },
    LiveTriviaSelector: {
        type: 'liveTriviaSelector',
        label: 'Live Trivia Selector',
        defaultVarType: VAR_TYPES.numberType,
        liveControl: true,
    },
    LiveTriviaStatus: {
        type: 'liveTriviaStatus',
        label: 'Live Trivia Status',
        defaultVarType: VAR_TYPES.customisationType,
        liveControl: true,
    },
    SlidePuzzleSelector: {
        type: 'slidePuzzleSelector',
        label: 'Slide Puzzle Selector',
        defaultVarType: VAR_TYPES.numberType,
        liveControl: true,
    },
    SlidePuzzleLiveState: {
        type: 'slidePuzzleLiveState',
        label: 'Slide Puzzle Live State',
        defaultVarType: VAR_TYPES.customisationType,
        liveControl: true,
    },
    UncontrolledVar: {
        type: 'uncontrolledVar',
        label: 'Uncontrolled Var',
        defaultVarType: VAR_TYPES.customisationType,
        liveControl: true,
    },

    LiveTextTemplateSelector: {
        type: 'liveTextTemplateSelector',
        label: 'Live Text Template Selector',
        defaultVarType: VAR_TYPES.numberType,
        liveControl: true,
    },
    LiveTextPresetSelector: {
        type: 'liveTextPresetSelector',
        label: 'Live Text Preset Selector',
        defaultVarType: VAR_TYPES.numberType,
        liveControl: true,
    },
    LiveTextMessageStatus: {
        type: 'liveTextLiveMessageStatus',
        label: 'Live Text Message Status',
        defaultVarType: VAR_TYPES.customisationType,
        liveControl: true,
    },
    LiveTextQueueStatus: {
        type: 'liveTextLiveQueueStatus',
        label: 'Live Text Queue Status',
        defaultVarType: VAR_TYPES.customisationType,
        liveControl: true,
    },
    LiveTextMessageError: {
        type: 'liveTextLiveMessageError',
        label: 'Live Text Message Error',
        defaultVarType: VAR_TYPES.customisationType,
        liveControl: true,
    },
    LiveTextResponseError: {
        type: 'liveTextResponseError',
        label: 'Live Text Response Error',
        defaultVarType: VAR_TYPES.customisationType,
        liveControl: true,
    },
    RandomiserStatus: {
        type: 'randomiserStatus',
        label: 'Randomiser Status',
        defaultVarType: VAR_TYPES.customisationType,
        liveControl: true,
    },
    RandomiserOptionsList: {
        type: 'randomiserOptionsList',
        label: 'Randomiser Options List',
        defaultVarType: VAR_TYPES.customisationType,
        liveControl: true,
    },
    RandomiserResultSelector: {
        type: 'randomiserResultSelector',
        label: 'Randomiser Result Selector',
        defaultVarType: VAR_TYPES.customisationType,
        liveControl: true,
    },
    RandomiserAddToReelSelector: {
        type: 'randomiserAddToReelSelector',
        label: 'Randomiser Add To Reel Selector',
        defaultVarType: VAR_TYPES.customisationType,
        liveControl: true,
    },
    RandomiserOptInData: {
        type: 'randomiserOptInData',
        label: 'Randomiser Opt In Data',
        defaultVarType: VAR_TYPES.customisationType,
        liveControl: true,
    },
    RandomiserPreviousResults: {
        type: 'randomiserPreviousResults',
        label: 'Randomiser Previous Results',
        defaultVarType: VAR_TYPES.customisationType,
        liveControl: true,
    },
    GenericFeatureStatus: {
        type: 'genericFeatureStatus',
        label: 'Generic Feature Status',
        defaultVarType: VAR_TYPES.customisationType,
        liveControl: true,
    },
    ExpeditionControls: {
        type: 'expeditionControls',
        label: 'Expedition Controls',
        defaultVarType: VAR_TYPES.customisationType,
        liveControl: true,
    },
    ExpeditionManagerControls: {
        type: 'expeditionManagerControls',
        label: 'Expedition Manager Controls',
        defaultVarType: VAR_TYPES.customisationType,
        liveControl: true,
    },
    ExpeditionActiveOverlaysControls: {
        type: 'expeditionActiveOverlaysControls',
        label: 'Expedition Active Overlays',
        defaultVarType: VAR_TYPES.customisationType,
        liveControl: true,
    },
    ExpeditionMessagesControls: {
        type: 'expeditionMessagesControls',
        label: 'Expedition Messages Controls',
        defaultVarType: VAR_TYPES.customisationType,
        liveControl: true,
    },
    ExpeditionStateControls: {
        type: 'expeditionStateControls',
        label: 'Expedition State Controls',
        defaultVarType: VAR_TYPES.customisationType,
        liveControl: true,
    },
    ImageOverlaysControls: {
        type: 'imageOverlaysControls',
        label: 'Image Overlays Controls',
        defaultVarType: VAR_TYPES.customisationType,
        liveControl: true,
    },
    ReadOnlyField: {
        type: 'readonlyField',
        label: "Read Only Field",
        liveControl: false,
        noDefaultVal: false,
        newFormatCustomisationField: true,
    },
    EditDataField: {
        type: 'editdataField',
        label: "Edit Data Field",
        liveControl: false,
        noDefaultVal: false,
        newFormatCustomisationField: true,
    },
}

export const POST_MESSAGE_OUT_TYPES = {
    CONNECTED: 'connected',
    HEARTBEAT: 'heartbeat',
  
    SETTINGS_VARS_RECEIVED: 'settings_vars_received',
    CUSTOMISATION_VARS_RECEIVED: 'customisation_vars_received',
    COMMAND_RECEIVED: 'command_received',
    STATE_UPDATE: 'state_update',

    // We are going to tell other disruptors when other features start / end so we can react to them
    FEATURE_STARTED: 'feature_started',
    FEATURE_ENDED: 'feature_ended',

    TRIGGERED_FEATURE_ENDED: 'triggered_feature_ended',

    QUIZ_DATA: 'quiz_data',

    STREAM_MARKER_CONFIRMED: 'stream_marker_confirmed',

    CLAW_SWITCH_CAM: 'claw_switch_cam',
    CLAW_MOVE_ENDED: 'claw_move_ended',
    CLAW_CONTROLLER_VOTE_RECEIVED: 'claw_controller_vote_received',

    TRIGGER_DATA_RECEIVED: 'trigger_data_received',

    PASS_DATA_TO_DISRUPTOR: 'pass_data_to_disruptor',

    EXPEDITION_CURRENT_STATE: 'expedition_current_state',
}

export const POST_MESSAGE_IN_TYPES = {
    HANDSHAKE: 'handshake',
    FEATURE_LOADED: 'feature_loaded',
    FEATURE_STARTED: 'feature_started',
    FEATURE_ENDED: 'feature_ended',
    CONTINUE_CHAIN: 'continue_chain',
    AUDIO_ENABLED: 'audio_enabled',
    TRIGGER_FEATURE: 'trigger_feature',
    TRIGGER_FEATURE_END: 'trigger_feature_end',

    REQUEST_QUIZ_DATA: 'request_quiz_data',
    LEADERBOARD_DATA: 'leaderboard_data',
    REPORT_ARCADE_DATA: 'report_arcade_data',

    SET_STREAM_MARKER: 'set_stream_marker',

    POST_LEADERBOARD_DATA: 'post_leaderboard_data',
    POST_FEATURE_DATA: 'post_feature_data',

    CLAW_START_SESSION: 'claw_start_session',
    CLAW_PLAY_MOVE: 'claw_play_move',
    CLAW_END_SESSION: 'claw_endsession',

    PASS_DATA_TO_DISRUPTOR: 'pass_data_to_disruptor',

    REQUEST_EXPEDITION_STATE: 'request_expedition_state',

    REPORT_PHASE_CHANGE: 'report_phase_change',
}

export const QUIZ_HELPER = {
    selectedQuiz:0
}
// Here for reference mostly, I guess
export const KOKO_TWITCH_STATES = {
    IDLE: 'idle',
    ACTIVE: 'active',
    END: 'end',
}

export const DATA_SAVE_LEVELS = {
    GLOBAL: 'global',
    STREAM: 'stream',
    CHANNEL: 'channel',
}
